import type { ExploreWidgetConfig } from '../../widgets/explorer/config'
import type { ExploreProperty } from '../../widgets/explorer/data'

export const DEFAULT_NO_DATES_LABEL = 'No dates available'

/**
 * Toolkit lowest-rate-availability port (#1064 Wave 4).
 *
 * Central's calendar-derived lowest / average nightly rates already exclude
 * unavailable and booked nights (`recompute_property_price_aggregates`:
 * is_available = 1 AND price > 0 AND date >= today) and come back `null` when
 * nothing is bookable; the live per-range `datesPrice` applies the same filter.
 * A card in an availability-aware price mode with no such rate used to fall
 * back to "Starting at" the base rate — the stale PMS field the toolkit module
 * exists to stop advertising. It now shows this label instead.
 *
 * Returns null when the operator opted out (`priceDisplay.showNoDatesLabel ===
 * false`) — the legacy base-rate fallback. The toolkit disables via a blank
 * label; here that cannot persist (a blank saved through the dashboard reaches
 * Central as null and is stripped at mount), so the opt-out is the boolean and
 * a blank or absent text means the default wording. Both leaves are optional
 * with no schema default (feed-global clobber rule), so defaults live here.
 */
export function noDatesLabel(options: Pick<ExploreWidgetConfig, 'priceDisplay'>): string | null {
  const pd = options.priceDisplay
  if (pd.showNoDatesLabel === false) return null
  const label = (pd.noDatesLabel ?? '').trim()
  return label.length > 0 ? label : DEFAULT_NO_DATES_LABEL
}

/**
 * True when Central has told us there is no bookable nightly rate: the explorer
 * list endpoint always sends `lowestPrice` / `averagePrice`, as a number or as
 * `null` when zero nights are bookable. An ABSENT key (`undefined`) means this
 * payload does not carry the field at all (other endpoints, fixtures) and keeps
 * the legacy base-rate fallback — the label only ever replaces a known-unbookable
 * rate, never a missing one. Non-positive numbers count as unbookable too
 * (toolkit rule: a $0 "Starting at" is the symptom the module exists to stop).
 */
export function rateUnbookable(rate: number | null | undefined): boolean {
  return rate !== undefined && (rate === null || !(rate > 0))
}

/**
 * Calendar-freshness gate for the label (central#3089). A null rate has three
 * causes the rate alone cannot separate: the calendar is fresh and every forward
 * night is unavailable (label), the calendar never synced, or its sync went stale
 * (say nothing — the legacy base-rate fallback). Central decides staleness
 * (`calendarStale`, relative to the platform's own sync interval) and counts the
 * bookable forward nights (`bookableNights`).
 *
 * Returns false when the label must be suppressed: Central flagged the calendar
 * stale; it reports open nights (`bookableNights > 0`, a null rate with open
 * nights means unpriced nights, not a full calendar); or it sent the count as
 * `null`, which per the central#3089 contract means no forward calendar rows at
 * all (never synced, or an empty calendar) — a stamp alone does not vouch for a
 * calendar Central has not counted. Only `bookableNights === 0` on a calendar
 * that is not stale says "full". Absent fields (`undefined`: payloads that
 * predate central#3089, other endpoints, fixtures) are not a signal and leave the
 * decision with the rate alone, as before.
 */
export function calendarFreshForLabel(
  property: Pick<ExploreProperty, 'bookableNights' | 'calendarStale'>,
): boolean {
  // Drift guards fail toward suppressing the label: any truthy verdict counts as
  // stale, and a count that is present but not a finite number counts as unknown.
  if (property.calendarStale) return false
  const nights = property.bookableNights
  if (nights === null) return false
  if (nights !== undefined) {
    const count = Number(nights)
    if (!Number.isFinite(count) || count > 0) return false
  }
  return true
}

/**
 * The label for one card, or null when it must not show: operator opt-out
 * (`noDatesLabel`) or a calendar Central has not vouched for
 * (`calendarFreshForLabel`). Both renderers read this one value, so the gate
 * reaches every label site in each tree.
 */
export function cardNoDatesLabel(
  options: Pick<ExploreWidgetConfig, 'priceDisplay'>,
  property: Pick<ExploreProperty, 'bookableNights' | 'calendarStale'>,
): string | null {
  if (!calendarFreshForLabel(property)) return null
  return noDatesLabel(options)
}

/**
 * The label's config read for surfaces OUTSIDE the explorer widget (#1188:
 * saved panel, standalone PriceRow). Those widgets carry no `priceDisplay` of
 * their own, so the opt-out and wording come from the feed-global explorer
 * settings (`feed.additional_info.explorer.priceDisplay`) — one knob covers
 * every surface that advertises the same rate. A widget-level explorer
 * override is deliberately not consulted: these surfaces belong to the feed,
 * not to any one explorer instance.
 *
 * Values arrive as raw JSON (this path has no zod parse), so the boolean read
 * mirrors `lenientBool`: only a literal `false` opts out; any other type is
 * "absent" and keeps the default-on behavior — exactly what the explorer's
 * parse would produce for the same stored value.
 */
export function feedGlobalNoDatesLabel(explorerGlobal: unknown): string | null {
  const pd = (explorerGlobal as {
    priceDisplay?: { showNoDatesLabel?: unknown; noDatesLabel?: unknown }
  } | null | undefined)?.priceDisplay
  if (pd?.showNoDatesLabel === false) return null
  const label = typeof pd?.noDatesLabel === 'string' ? pd.noDatesLabel.trim() : ''
  return label.length > 0 ? label : DEFAULT_NO_DATES_LABEL
}

/**
 * Gate + label in one for a non-explorer surface: the label only replaces a
 * rate Central says is unbookable (`rateUnbookable` on the calendar-derived
 * low) on a calendar Central vouches for (`calendarFreshForLabel`) — the same
 * two gates as the explorer card. Null means the caller keeps its legacy
 * base-rate fallback.
 */
export function feedNoDatesLabelForProperty(
  explorerGlobal: unknown,
  property: Pick<ExploreProperty, 'lowestPrice' | 'bookableNights' | 'calendarStale'>,
): string | null {
  if (!rateUnbookable(property.lowestPrice)) return null
  if (!calendarFreshForLabel(property)) return null
  return feedGlobalNoDatesLabel(explorerGlobal)
}
